<template>
    <div class="relative flex flex-col gap-2 items-start py-4">
        <div class="flex w-full">
            <div class="avatar indicator mr-3 lg:mr-4">
                <div class="w-20 h-20 lg:w-[100px] lg:h-[100px]">
                    <MiscLink no-prefetch :to="productUrl">
                        <NuxtImg
                            :src="productMedia?.[0]?.media?.path"
                            :alt="productMedia?.[0]?.media?.alt"
                            width="100"
                            height="100"
                            loading="lazy"
                            :placeholder="'/images/loader.gif'"
                            class="no-forced-colors:mix-blend-multiply"
                        />
                    </MiscLink>
                </div>
            </div>
            <div class="flex flex-col justify-between gap-4 w-full lg:flex-row lg:flex-wrap lg:relative lg:gap-7">
                <div class="w-full flex justify-between gap-2 lg:w-auto">
                    <div class="w-full flex flex-col gap-1 text-sm leading-tight">
                        <MiscLink :to="productUrl" class="font-semibold">
                            {{ excentosConfig != null ? excentosConfig['Name'] : product.name }}
                        </MiscLink>
                        <div v-if="product.productNumber != null" class="text-base-content-subtle">
                            {{ `${$t('wishlist.product.articleNumber')} ${product.productNumber}` }}
                        </div>
                        <div v-if="excentosConfig != null || (product.options && product.options.length > 0)" class="flex flex-row flex-wrap gap-1">
                            <template v-if="excentosConfig != null">
                                <template
                                    v-for="(attribute, excentosAttributeIndex) in excentosConfigKeys"
                                    :key="attribute"
                                >
                                    <span
                                        v-if="!excentosDisplayKeyBlacklist.includes(attribute)"
                                        class="text-sm leading-4 font-medium text-base-content-subtle"
                                    >{{ attribute.replaceAll('_', ' ') }}: {{ excentosConfig[attribute] }}<span v-if="excentosAttributeIndex < excentosConfigKeys.length - 1">,</span></span>
                                </template>
                            </template>
                            <template v-else>
                                <template
                                    v-for="(option, optionIndex) in product.options"
                                    :key="option.id"
                                >
                                    <div
                                        class="text-sm leading-4"
                                        :class="option.group?.name === 'Right/left' ? 'font-semibold text-neutral' : 'font-normal text-base-content-subtle'"
                                    >
                                        {{ getTranslatedProperty(option, 'name') }}<span v-if="optionIndex < product.options!.length - 1">,</span>
                                    </div>
                                </template>
                            </template>
                        </div>
                    </div>
                </div>
                <div v-if="unitPrice != null" class="w-full text-sm leading-5 font-semibold lg:w-auto lg:pr-12">
                    <div :class="{ 'text-sale': isListPrice }">
                        <span v-if="displayFrom">{{ $t('product.listing.card.from') }} </span>
                        {{ getFormattedPrice(unitPrice) }}
                    </div>
                    <div v-if="isListPrice" class="line-through text-base-content-subtle font-normal">
                        {{ getFormattedPrice(price?.listPrice?.price) }}
                    </div>
                </div>
                <div class="flex flex-wrap gap-4 items-center lg:w-full" :class="{ 'border-b border-secondary pb-8': !isLast }">
                    <div v-if="product.available" class="flex gap-3 justify-start items-center">
                        <label :for="`wishlistItems[${product.id}][quantity]`">
                            <span class="label-text font-semibold">{{ $t('wishlist.product.quantity') }}</span>
                        </label>
                        <input
                            :id="`wishlistItems[${product.id}][quantity]`"
                            v-model.number="quantity"
                            type="number"
                            :name="`wishlistItems[${product.id}][quantity]`"
                            :min="product.minPurchase"
                            :max="product.calculatedMaxPurchase"
                            :step="product.purchaseSteps"
                            class="input w-16 text-center"
                        >
                    </div>

                    <button
                        type="button"
                        data-cy="removeWishlistItem"
                        class="btn btn-ghost btn-circle lg:absolute lg:-top-3 lg:-right-3"
                        :class="{ 'ml-auto md:ml-0': product.available }"
                        :aria-label="$t('wishlist.product.toggleButton.removeLabel')"
                        @click="onRemoveProduct"
                    >
                        <MiscIcon name="trash" class="w-5 h-5" />
                    </button>
                    <button
                        v-if="product.available"
                        :disabled="cartLoading"
                        type="button"
                        :aria-disabled="cartLoading"
                        class="btn btn-primary"
                        @click="onClickAddToCart"
                    >
                        <span v-if="cartLoading" class="loading" />
                        <MiscIcon v-else name="shopping-cart" />
                        <span>{{ $t('wishlist.product.addToCart') }}</span>
                    </button>
                    <button
                        v-if="excentosConfiguratorUrl != null"
                        type="button"
                        data-cy="reopenExcentosConfigurator"
                        class="btn btn-secondary"
                        @click="emit('openConfigurator', excentosConfiguratorUrl)"
                    >
                        {{ $t('wishlist.product.changeConfiguration') }}
                    </button>
                    <MiscLink
                        :to="productUrl"
                        class="link text-sm font-semibold"
                        :class="{ 'ml-auto md:ml-0': product.available }"
                    >
                        {{ $t('wishlist.product.change') }}
                    </MiscLink>
                    <div v-if="!product.available" data-cy="productSoldOutWishlist" class="alert alert-error">
                        <MiscIcon name="minus-circle" />
                        <span>{{ $t('wishlist.product.notAvailable') }}</span>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import type { Schemas } from '#shopware'
import { getProductRoute, getTranslatedProperty } from '@shopware/helpers'

const props = defineProps<{
    product: Schemas['Product']
    isLast: boolean
    excentosConfiguration?: string
}>()

const emit = defineEmits<{
    openConfigurator: [scriptUrl: string]
}>()

const localePath = useLocalePath()
const { formatLink } = useInternationalization(localePath)
const { removeFromWishlist } = useProductWishlist(props.product.id)
const productRef = computed(() => props.product)
const { addToCart, quantity } = useAddToCart(productRef)
const { displayFrom, unitPrice, isListPrice, price } = useProductPriceCustom(productRef)
const sidenav = useSidenav()
const { getFormattedPrice } = usePrice()
const { showNotification } = useNotification()
const nuxtApp = useNuxtApp()

const cartLoading = ref(false)

const productUrl = computed(() => formatLink(getProductRoute(props.product)))
const productMedia = computed(() => sortProductMedia(props.product.media))

// Mirrors CartListItem.vue's Excentos display: overrides the product name and shows the
// configuration's attributes in place of the regular variant options.
const excentosConfig = computed(() => parseExcentosConfiguration(props.excentosConfiguration))
const excentosConfigKeys = computed(() => Object.keys(excentosConfig.value ?? {}))
const excentosDisplayKeyBlacklist = EXCENTOS_DISPLAY_KEY_BLACKLIST

// The modal injects what it is handed as a `<script>`, so the saved configuration's backlink
// only works in Excentos' script-serving `loadadvisor` form; see `excentosBackLinkToScriptUrl`.
const excentosConfiguratorUrl = computed(() => excentosBackLinkToScriptUrl(
    extractExcentosBackLink(excentosConfig.value),
))

async function onRemoveProduct () {
    try {
        await removeFromWishlist()
        nuxtApp.callHook('removeFromWishlist', { product: props.product })
    } catch (e) {
        showNotification(e instanceof Error ? e.message : 'An error occurred', 'error', true)
    }
}

async function onClickAddToCart () {
    cartLoading.value = true
    const addedQty = quantity.value
    try {
        const payload = props.excentosConfiguration != null
            ? { custom_cart_excentos_configuration: props.excentosConfiguration }
            : undefined
        await addToCart(payload)
        sidenav.open('cart')
        nuxtApp.callHook('addToCart', { addQty: addedQty, product: props.product, action: 'wishlist' })
    } catch (e) {
        showNotification(e instanceof Error ? e.message : 'An error occurred', 'error', true)
    } finally {
        cartLoading.value = false
    }
}
</script>
